import { parseOpenApi, deriveRiskModel } from "@keystone/intake";
import { designServer } from "@keystone/designer";
import { generateDesigned } from "@keystone/generator";
import { harden, scanForPoisoning } from "@keystone/hardener";
import { runRedteam } from "@keystone/redteam";
import { ToolRuntime } from "@keystone/eval";
import type { ToolDesign } from "@keystone/core";
import type { PipelineStage, StageState } from "./components/PipelineCanvas.js";

/**
 * Staged pipeline driver. Yields a status update after each stage so the
 * PipelineCanvas can repaint between them and the user sees real, sequential
 * progress (not a single-frame state flip). Returns the built design + the
 * in-browser runtime + a fact for each stage to display next to its node.
 *
 * Design intent (per outsider review): every yield is driven by REAL work
 * finishing, not by `setTimeout`. The visible suspense comes from the
 * artifacts streaming in (tool names, attack strings, finding descriptions),
 * not from sleeps. The only sleeps left are `yield` boundaries the JS
 * scheduler needs to repaint — `requestAnimationFrame` via `await Promise.resolve()`.
 */

export interface BuildEvent {
  stage: PipelineStage;
  state: StageState;
  fact?: string;
  /** Optional streamable artifact emitted while the stage is `running`. */
  artifact?: PipelineArtifact;
}

export type PipelineArtifact =
  | { kind: "tool"; name: string; readOnly: boolean; destructive: boolean }
  | { kind: "finding"; ruleId: string; severity: string; toolName: string; rationale: string }
  | { kind: "attack"; id: string; description: string; verdict: "blocked" | "succeeded" };

export interface BuildResult {
  design: ToolDesign;
  runtime: ToolRuntime;
  apiTitle: string;
  toolCount: number;
  scannerFindings: number;
  redteamConfirmed: number;
  redteamAttempted: number;
  redteamBlockedPct: number;
  serverDigest: string;
  /** Full artifact list for the post-build ledger / sidebar. */
  artifacts: PipelineArtifact[];
}

/** Yield to the browser so React can repaint before the next stage runs. */
const yieldFrame = (): Promise<void> =>
  new Promise((r) => {
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => r());
    else setTimeout(r, 0);
  });

export async function* runBuild(spec: unknown): AsyncGenerator<BuildEvent, BuildResult, void> {
  const artifacts: PipelineArtifact[] = [];

  yield { stage: "intake", state: "running" };
  await yieldFrame();
  const cap = parseOpenApi(spec);
  if (cap.operations.length === 0) {
    yield { stage: "intake", state: "error", fact: "0 operations" };
    throw new Error("Spec has no operations under `paths` — nothing to build.");
  }
  const risk = deriveRiskModel(cap);
  const destructiveOps = risk.operations.filter((o) => o.level === "destructive").length;
  yield {
    stage: "intake",
    state: "done",
    fact: `${cap.operations.length} ops${destructiveOps > 0 ? ` · ${destructiveOps} destructive` : ""}`
  };

  yield { stage: "design", state: "running" };
  await yieldFrame();
  const designed = designServer(cap, risk, { serverName: "live-mcp", serverVersion: "1.0.0" }).design;
  for (const t of designed.tools) {
    const art: PipelineArtifact = {
      kind: "tool",
      name: t.name,
      readOnly: t.annotations.readOnlyHint === true,
      destructive: t.annotations.destructiveHint === true
    };
    artifacts.push(art);
    yield { stage: "design", state: "running", artifact: art };
    await yieldFrame();
  }
  yield { stage: "design", state: "done", fact: `${designed.tools.length} tools` };

  yield { stage: "harden", state: "running" };
  await yieldFrame();
  const desArt = generateDesigned(designed, { generatedAt: new Date(0).toISOString() });
  const hardened = harden({ design: designed, artifact: desArt, config: { failThreshold: "high" } });
  const findings = hardened.report?.findings ?? scanForPoisoning({ design: designed });
  for (const f of findings.slice(0, 8)) {
    const art: PipelineArtifact = {
      kind: "finding",
      ruleId: f.ruleId,
      severity: f.severity,
      toolName: f.toolName,
      rationale: f.rationale
    };
    artifacts.push(art);
    yield { stage: "harden", state: "running", artifact: art };
    await yieldFrame();
  }
  const highs = findings.filter((f) => f.severity === "high" || f.severity === "critical").length;
  yield {
    stage: "harden",
    state: "done",
    fact: findings.length === 0 ? "no poisoning detected" : `${findings.length} findings (${highs} HIGH+)`
  };

  yield { stage: "audit", state: "running" };
  await yieldFrame();
  const redD = runRedteam({ design: designed, artifact: hardened.artifact, hardened: true });
  const redN = runRedteam({ design: designed, artifact: desArt, hardened: false });
  // Pair findings by id so we can report each attempted attack's verdict.
  const designedById = new Map(redD.findings.map((f) => [f.id, f]));
  for (const naiveFinding of redN.findings.slice(0, 8)) {
    if (!naiveFinding.exploitConfirmed) continue; // only stream attacks that work against naive
    const d = designedById.get(naiveFinding.id);
    const verdict: "blocked" | "succeeded" = d && !d.exploitConfirmed ? "blocked" : "succeeded";
    const art: PipelineArtifact = {
      kind: "attack",
      id: naiveFinding.id,
      description: naiveFinding.description,
      verdict
    };
    artifacts.push(art);
    yield { stage: "audit", state: "running", artifact: art };
    await yieldFrame();
  }
  const blocked = redN.summary.confirmed - redD.summary.confirmed;
  const pct = redN.summary.confirmed === 0 ? 100 : Math.round((blocked / redN.summary.confirmed) * 100);
  yield { stage: "audit", state: "done", fact: `${pct}% blocked (${blocked}/${redN.summary.confirmed})` };

  yield { stage: "attest", state: "running" };
  await yieldFrame();
  const digest = hardened.artifact.digest;
  yield { stage: "attest", state: "done", fact: digest.slice(0, 12) };

  yield { stage: "deploy", state: "running" };
  await yieldFrame();
  const runtime = new ToolRuntime(designed);
  yield { stage: "deploy", state: "done", fact: "in-browser runtime" };

  return {
    design: designed,
    runtime,
    apiTitle: cap.apiTitle,
    toolCount: designed.tools.length,
    scannerFindings: findings.length,
    redteamConfirmed: redD.summary.confirmed,
    redteamAttempted: redN.summary.attempted,
    redteamBlockedPct: pct,
    serverDigest: digest,
    artifacts
  };
}
